/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import Layout from "../../../../Layouts/Layout";
import LandingPageTeaser from "../../../../../components/Teaser/LandingPageTeaser";
import CustomerFeedback from "@/src/components/Sections/Generic/CustomerFeedback/CustomerFeedback";
import AdvantagesLegalbirdAttorneySection from "../../../../../components/Sections/Generic/AdvantagesLegalbirdAttorneySection/AdvantagesLegalbirdAttorneySection";
import AttorneySection from "../../../../../components/Sections/Generic/AttorneySection/AttorneySection";
import IncorrectPenaltySection from "../../../../../components/Sections/Traffic/IncorrectPenaltySection/IncorrectPenaltySection";
import TrafficCheckin from "../../../../../components/Checkin/TrafficCheckin/TrafficCheckin";
import Container from "../../../../../components/Container/Container";
import PartnerLogoSection from "../../../../../components/Sections/Generic/PartnerLogoSection/PartnerLogoSection";
import TrafficCostRiskSection from "../../../../../components/Sections/Traffic/TrafficCostRiskSection/TrafficCostRiskSection";
import LpFineDrivingBanPartnerMeta from "./LpFineDrivingBanPartnerMeta";
import TrafficCheckinPartnerInsurer from "../../../../../components/Checkin/TrafficCheckin/TrafficCheckinPartnerInsurer";
import { trafficTeaserCards } from "@/src/services/landingPageService";
import LinkedCardBox from "../../../../../components/LinkedCardBox/LinkedCardBox";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { Partner } from "@/src/types/Partner";
import HowItWorksSectionTraffic from "../../../../../components/Sections/Traffic/HowItWorksSection/HowItWorksSectioTraffic";
import { PARTNER_IRIS } from "@/src/constants/partnerIris";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/src/services/reactQuery/reactQueryService";
import ApiClient from "../../../../../services/apiClient";

const LpFineDrivingBanPartner = ({ partnerIri }: { partnerIri: NonNullable<AbstractCase["acquisitionPartner"]> }) => {
  const { data: partner } = useQuery({
    queryKey: [...queryKeys.all(partnerIri), "displayName"],

    queryFn: async (): Promise<Partner | null> => {
      return (await ApiClient.get(partnerIri)) as Partner;
    },
  });

  // Partner config
  let hasInsurerCheckin;
  let partnerFullName = partner?.brandName ?? partner?.name ?? "Partner";
  switch (partnerIri) {
    case PARTNER_IRIS.WBS:
      hasInsurerCheckin = false;
      break;
    case PARTNER_IRIS.ARAG:
      hasInsurerCheckin = true;
      break;
    case PARTNER_IRIS.ADVOCARD:
      hasInsurerCheckin = true;
      break;
    default:
      hasInsurerCheckin = false;
  }

  let switchChecking = (hasInsurerCheckin: boolean, partner: NonNullable<AbstractCase["acquisitionPartner"]>) => {
    if (hasInsurerCheckin) {
      return (
        <TrafficCheckinPartnerInsurer
          partner={partner}
          idPrefix={"top"}
          checkinHeading={"Wir geben Ihnen eine kostenlose Ersteinschätzung! Was wird Ihnen vorgeworfen?"}
          accusation={{
            name: "accusation",
            detail: {
              name: "accusation",
              label: "Vorwurf auswählen",
              options: [
                { value: "speeding", label: "Geschwindigkeitsverstoß" },
                { value: "red_light", label: "Rotlichtverstoß" },
                { value: "distance", label: "Abstandsverstoß" },
                { value: "other", label: "Handy / Drogen / Parken / Sonstiges" },
              ],
            },
          }}
        />
      );
    }
    if (!hasInsurerCheckin) {
      return (
        <TrafficCheckin
          partner={partner}
          idPrefix={"top"}
          checkinHeading={"Wir geben Ihnen eine kostenlose Ersteinschätzung! Was wird Ihnen vorgeworfen?"}
          accusation={{
            name: "accusation",
            detail: {
              name: "accusation",
              label: "Vorwurf auswählen",
              options: [
                { value: "speeding", label: "Geschwindigkeitsverstoß" },
                { value: "red_light", label: "Rotlichtverstoß" },
                { value: "distance", label: "Abstandsverstoß" },
                { value: "other", label: "Handy / Drogen / Parken / Sonstiges" },
              ],
            },
          }}
        />
      );
    }
  };

  const { isLoggedIn } = useCustomer();

  return (
    <Layout minimal={!isLoggedIn} clickableLogo={isLoggedIn} backgroundType={"landingpage"}>
      <LpFineDrivingBanPartnerMeta partnerFullName={partnerFullName} />
      <LandingPageTeaser
        headline={"Jetzt Einspruch einlegen!"}
        subheadline={"Effektiv gegen Bußgeld, Punkte oder Fahrverbot vorgehen"}
        teaserButtonText={"Kostenlose Ersteinschätzung"}
        teaserButtonLink={isLoggedIn ? "/servicewelt" : "#top-checkin"}
      >
        <LinkedCardBox linkedCards={trafficTeaserCards} />
      </LandingPageTeaser>
      <PartnerLogoSection containerStyle={"settlementContainer"} partnerIri={partnerIri} />
      {!isLoggedIn && <Container>{switchChecking(hasInsurerCheckin, partnerIri)}</Container>}
      <IncorrectPenaltySection />
      <HowItWorksSectionTraffic />
      <TrafficCostRiskSection />
      <AttorneySection
        product={"traffic"}
        subheading={"Sie möchten professionell und unkompliziert gegen Ihren Bescheid vorgehen? Wir helfen Ihnen - jederzeit und ortsunabhängig!"}
      />
      <AdvantagesLegalbirdAttorneySection product={"traffic"} />
      <CustomerFeedback />
      {!isLoggedIn && <Container>{switchChecking(hasInsurerCheckin, partnerIri)}</Container>}
    </Layout>
  );
};

export default LpFineDrivingBanPartner;
